import { createContext, FC, useContext, useEffect, useMemo, useReducer, useState } from 'react';
import { filterNavLinksForAccess, salesLandingLinks } from '../helpers';
import { UserContext } from './user';
import { useFlags } from '../hooks';

interface ISalesUserContext {
  selectedSalesTab: string;
  setSelectedSalesTab: (val: string) => void;
  perPageSalesPortalValues: perPagePayload[];
  dispatchPerPage: React.Dispatch<SalesPageReducerAction>;
}

export const SalesContext = createContext<ISalesUserContext>({
  selectedSalesTab: '',
  setSelectedSalesTab: () => {},
  perPageSalesPortalValues: [],
  dispatchPerPage: () => {}
});

interface ISalesContextHandlerProps {}

export const SalesContextHandler: FC<ISalesContextHandlerProps> = ({ children }): JSX.Element => {
  const featureFlags = useFlags();
  const userContext = useContext(UserContext);
  const [selectedSalesTab, setSelectedSalesTab] = useState<string>(
    filterNavLinksForAccess(salesLandingLinks, userContext, featureFlags)?.[0]?.name
  );

  useEffect(() => {
    setSelectedSalesTab(
      filterNavLinksForAccess(salesLandingLinks, userContext, featureFlags)?.[0]?.name ??
        salesLandingLinks?.[0]?.name
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Maintain state of "perPage" values
  const [perPageSalesPortalValues, dispatchPerPage] = useReducer(perPageReducer, [], initializer);

  useEffect(() => {
    localStorage.setItem('salesPerPage', JSON.stringify(perPageSalesPortalValues));
  }, [perPageSalesPortalValues]);

  const salesProviderValues = useMemo(() => {
    return { selectedSalesTab, setSelectedSalesTab, perPageSalesPortalValues, dispatchPerPage };
  }, [selectedSalesTab, setSelectedSalesTab, perPageSalesPortalValues, dispatchPerPage]);

  return <SalesContext.Provider value={salesProviderValues}>{children}</SalesContext.Provider>;
};

// https://stackoverflow.com/questions/64547044/persist-localstorage-with-usereducer
type perPagePayload = {
  pageName: string;
  environment: string;
  itemsPerPage: number;
};

const UPDATE = 'UPDATE_PERPAGE';
type SalesPageReducerAction = {
  type: string;
  payload: perPagePayload;
};

const initialState = [];

export const initializer = (initialValue = initialState) =>
  JSON.parse(localStorage.getItem('salesPerPage')) || initialValue;

export const perPageReducer = (state: perPagePayload[], action: SalesPageReducerAction) => {
  switch (action.type) {
    case UPDATE:
      const newState = state.find(
        item =>
          item.pageName === action.payload.pageName &&
          item.environment === action.payload.environment
      )
        ? state.map(item => {
            return item.pageName === action.payload.pageName
              ? {
                  environment: action.payload.environment,
                  itemsPerPage: action.payload.itemsPerPage,
                  pageName: action.payload.pageName
                }
              : item;
          })
        : [...state, { ...action.payload }];
      return newState;

    default:
      return state;
  }
};

export const update = (item: perPagePayload) => ({
  type: UPDATE,
  payload: item
});
